import React, {useState, useEffect, useRef} from "react"
import {Row} from "@irati/ui/src/Layout"
import {debounce} from "lodash"

import {FormattedMessage} from "@irati/utils/lang"
import scrollAtBottom from "@irati/utils/scroll/scrollAtBottom"
import {DisablePointerEventsOnScroll} from "@irati/ui/src/DisablePointerEventsOnScroll"
import Loader from "@irati/utils/services/apollo/Loading/Loader"
import FeedQuery, {feedQuery} from "@irati/recognition/apollo/query/Feed"
import {H3} from "@irati/ui/src/Typography/Heading"

import {RecognitionFeed} from "@irati/types"
import {FeedItemType} from "@irati/recognition/enums"
import LoadMore from "../LoadMore"
import Card from "./ClosedPoll/Card"
import {EmptyFeed} from "./Empty"
import {FiltersWrapper} from "./Filters/styled"
import Filters from "./Filters"
import {StyledCol, HeaderWrapper, Feed} from "./styled"

const SUPPORTED_FEED_CARDS = [FeedItemType.RECOGNITION, FeedItemType.POLL, FeedItemType.GROUP_RECOGNITION]
const filterSupportedFeedTypes = (feedEntity: RecognitionFeed) => {
  return SUPPORTED_FEED_CARDS.includes(feedEntity.type)
}
const ClosedPolls = () => {
  const [variables, setVariables] = useState(feedQuery.variables)
  const [hasRefetchingError, setHasRefetchingError] = useState(false)
  const onLoadMore = useRef()

  useEffect(() => {
    window.addEventListener("online", handleNetworkStatusChange)
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("online", handleNetworkStatusChange)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const handleVisibilityChange = (view) => {
    setVariables({
      ...feedQuery.variables,
      view
    })
  }

  const handleScroll = debounce(() => {
    const atBottom = scrollAtBottom()

    if (atBottom && !hasRefetchingError && onLoadMore.current) {
      doFetchMore()
    }
  }, 200)

  const handleNetworkStatusChange = () => {
    if (navigator.onLine && hasRefetchingError) {
      doFetchMore()
    }
  }

  const doFetchMore = () => {
    setHasRefetchingError(false)
    onLoadMore.current?.().catch(() => setHasRefetchingError(true))
  }

  const hasReceivedData = (data) => {
    return data && "recognitionFeed" in data && "results" in data.recognitionFeed && data.recognitionFeed.results.length > 0
  }

  const renderFilters = () => {
    const {view} = variables
    return (
      <FiltersWrapper>
        <Filters onVisibilityChange={handleVisibilityChange} view={view} />
      </FiltersWrapper>
    )
  }

  const feedProps = {...feedQuery, variables}

  return (
    <Row>
      <StyledCol xs={12}>
        <HeaderWrapper>
          <H3>
            <FormattedMessage id="recognition.polls.closedPolls.label" />
          </H3>
        </HeaderWrapper>
        <DisablePointerEventsOnScroll>
          <FeedQuery {...feedProps}>
            {({data, fetchMore, refetch, loading: isLoading, error, networkStatus}) => {
              const hasData = hasReceivedData(data)
              const hasError = error || hasRefetchingError
              const isFetchingMore = networkStatus === 3

              const cursor = data && data.recognitionFeed ? data.recognitionFeed.cursor : ""
              const fetchMoreVariables = {
                ...variables,
                cursor
              }
              onLoadMore.current = !isLoading ? () => fetchMore(fetchMoreVariables) : null

              return (
                <>
                  {renderFilters()}
                  {isLoading && !isFetchingMore ? (
                    <Loader />
                  ) : (
                    <>
                      {hasData && <Feed component={Card} arr={data.recognitionFeed.results.filter(filterSupportedFeedTypes)} />}
                      {isLoading && <Loader />}
                      {!isLoading && hasError && <LoadMore onClick={error ? () => refetch() : fetchMore} />}
                      {!isLoading && !hasError && !hasData && <EmptyFeed />}
                    </>
                  )}
                </>
              )
            }}
          </FeedQuery>
        </DisablePointerEventsOnScroll>
      </StyledCol>
    </Row>
  )
}

export default ClosedPolls
